import React, { useState, useEffect } from 'react';
import styles from './DetailPanel.module.css';
import { fmtSize, fmtTime, timeClass, copyToClipboard } from '../utils/harUtils';

const TABS = ['Overview', 'Req Headers', 'Res Headers', 'Body', 'Params'];

export default function DetailPanel({ request, onClose, onToast }) {
  const [tab, setTab] = useState('Overview');

  useEffect(() => {
    setTab('Overview');
  }, [request]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!request) return null;
  const r = request;

  const copyUrl = () => {
    copyToClipboard(r.url).then(() => onToast('✓ Full URL Copied!'));
  };

  const renderTab = () => {
    switch (tab) {
      case 'Overview':
        return (
          <div>
            <div className={styles.infoGrid}>
              <InfoBox label="Status" value={r.status || '—'}
                color={r.status >= 400 ? 'var(--red)' : r.status >= 300 ? 'var(--warn)' : 'var(--green)'} />
              <InfoBox label="Method" value={r.method} />
              <InfoBox label="Size" value={fmtSize(r.size)} />
              <InfoBox label="Time" value={fmtTime(r.time)}
                color={r.time < 500 ? 'var(--green)' : r.time < 2000 ? 'var(--warn)' : 'var(--red)'} />
              <InfoBox label="Domain" value={r.domain} small />
              <InfoBox label="Started" value={r.startedAt ? new Date(r.startedAt).toLocaleTimeString() : '—'} small />
            </div>
            {Object.keys(r.timings).length > 0 && (
              <>
                <div className={styles.sectionTitle}>Timings</div>
                <KVTable rows={Object.entries(r.timings)
                  .filter(([, v]) => v > 0)
                  .map(([k, v]) => [k, Math.round(v) + ' ms'])}
                  keyColor="var(--text2)" />
              </>
            )}
            <div className={styles.sectionTitle}>MIME Type</div>
            <div className={styles.mimeType}>{r.mimeType || '—'}</div>
            {r.httpVersion && <>
              <div className={styles.sectionTitle}>Protocol</div>
              <div className={styles.mimeType}>{r.httpVersion}</div>
            </>}
          </div>
        );

      case 'Req Headers':
        return r.reqHeaders.length
          ? <KVTable rows={r.reqHeaders.map(h => [h.name, h.value])} keyColor="var(--accent2)" />
          : <Empty msg="No request headers captured" />;

      case 'Res Headers':
        return r.resHeaders.length
          ? <KVTable rows={r.resHeaders.map(h => [h.name, h.value])} keyColor="var(--accent)" />
          : <Empty msg="No response headers captured" />;

      case 'Body': {
        let postContent = null;
        if (r.postData) {
          let text = r.postData.text || '';
          try { text = JSON.stringify(JSON.parse(text), null, 2); } catch (_) {}
          postContent = (
            <>
              <div className={styles.sectionTitle}>Request Body ({r.postData.mimeType || 'unknown'})</div>
              <pre className={styles.codeBlock}>{text}</pre>
            </>
          );
        }
        let resContent = null;
        if (r.resBody) {
          let bodyText = r.resBody;
          if (r.resEncoding === 'base64') {
            bodyText = '[base64 encoded — binary content]';
          } else {
            try { bodyText = JSON.stringify(JSON.parse(bodyText), null, 2); } catch (_) {}
          }
          resContent = (
            <>
              <div className={styles.sectionTitle} style={{ marginTop: 16 }}>Response Body</div>
              <pre className={styles.codeBlock}>{bodyText.slice(0, 15000)}{bodyText.length > 15000 ? '\n\n… [truncated for display]' : ''}</pre>
            </>
          );
        }
        if (!postContent && !resContent) return <Empty msg="No body data captured in this HAR entry" />;
        return <div>{postContent}{resContent}</div>;
      }

      case 'Params': {
        const hasQuery = r.queryParams.length > 0;
        const hasPost = r.postData?.params?.length > 0;
        if (!hasQuery && !hasPost) return <Empty msg="No query or post parameters found" />;
        return (
          <div>
            {hasQuery && <>
              <div className={styles.sectionTitle}>Query Parameters</div>
              <KVTable rows={r.queryParams.map(p => [p.name, p.value])} keyColor="var(--warn)" />
            </>}
            {hasPost && <>
              <div className={styles.sectionTitle} style={{ marginTop: 16 }}>Post Parameters</div>
              <KVTable rows={r.postData.params.map(p => [p.name, p.value || ''])} keyColor="var(--blue)" />
            </>}
          </div>
        );
      }

      default: return null;
    }
  };

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <div className={styles.panelMeta}>
            <div className={styles.metaRow}>
              <span className={`${styles.methodBadge} ${styles['method_' + r.method]}`}>{r.method}</span>
              <span className={styles.statusBadge}
                style={{ color: r.status >= 400 ? 'var(--red)' : 'var(--green)' }}>
                {r.status || '—'}
              </span>
              <span className={styles.typeBadge}>{r.typeCategory}</span>
              {r.httpVersion && <span className={styles.proto}>{r.httpVersion}</span>}
            </div>
            <div className={styles.urlBox} onClick={copyUrl} title="Click to copy full URL">
              <span className={styles.urlText}>{r.url}</span>
              <span className={styles.urlCopyHint}>📋 click to copy</span>
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div className={styles.tabs}>
          {TABS.map(t => (
            <button
              key={t}
              className={`${styles.tab} ${tab === t ? styles.tabActive : ''}`}
              onClick={() => setTab(t)}
            >{t}</button>
          ))}
        </div>

        <div className={styles.panelContent}>
          {renderTab()}
        </div>
      </div>
    </>
  );
}

function InfoBox({ label, value, color, small }) {
  return (
    <div className={styles.infoBox}>
      <div className={styles.infoLabel}>{label}</div>
      <div className={styles.infoVal} style={{
        color: color || 'var(--text)',
        fontSize: small ? '11px' : '14px',
        wordBreak: 'break-all',
      }}>{value}</div>
    </div>
  );
}

function KVTable({ rows, keyColor }) {
  return (
    <table className={styles.kvTable}>
      <tbody>
        {rows.map(([k, v], i) => (
          <tr key={i} className={styles.kvRow}>
            <td className={styles.kvKey} style={{ color: keyColor }}>{k}</td>
            <td className={styles.kvVal}>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Empty({ msg }) {
  return <div className={styles.emptyMsg}>{msg}</div>;
}
